import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Switch,
  FormControlLabel, IconButton, CircularProgress, Chip,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

import {
  createMarketplaceItem,
  updateMarketplaceItem,
  uploadGalleryImage,
  removeGalleryImage,
  uploadVideoFile,
  listCategories,
} from "../../services/appMarketplaceService";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  coverPreview: {
    width: "100%",
    maxHeight: 200,
    objectFit: "cover",
    borderRadius: 8,
    marginTop: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  galleryGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  galleryThumb: {
    position: "relative",
    width: 90,
    height: 70,
  },
  galleryImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
  },
  galleryDelete: {
    position: "absolute",
    top: 2,
    right: 2,
    padding: 2,
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    "&:hover": { background: "rgba(200,0,0,0.8)" },
  },
  sectionLabel: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: 4,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  quillWrapper: {
    "& .ql-editor": { minHeight: 120 },
    marginTop: 4,
  },
}));

const emptyForm = {
  title: "",
  shortDescription: "",
  description: "",
  category: "",
  videoUrl: "",
  buttonText: "Saiba Mais",
  buttonUrl: "",
  active: true,
  sortOrder: 0,
};

const AppMarketplaceModal = ({ open, onClose, item, onSaved }) => {
  const classes = useStyles();
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoFileName, setVideoFileName] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);

  const isEdit = Boolean(item?.id);

  useEffect(() => {
    listCategories()
      .then(({ data }) => setCategories(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      if (item) {
        setForm({
          title: item.title || "",
          shortDescription: item.shortDescription || "",
          description: item.description || "",
          category: item.category || "",
          videoUrl: item.videoUrl || "",
          buttonText: item.buttonText || "Saiba Mais",
          buttonUrl: item.buttonUrl || "",
          active: item.active !== false,
          sortOrder: item.sortOrder || 0,
        });
        setCoverPreview(item.coverImage ? `${backendUrl}/public/${item.coverImage}` : null);
        setGallery(item.gallery || []);
        setCoverFile(null);
        setVideoFile(null);
        setVideoFileName(item.videoFile ? item.videoFile.split("/").pop() : "");
      } else {
        setForm(emptyForm);
        setCoverPreview(null);
        setCoverFile(null);
        setVideoFile(null);
        setVideoFileName("");
        setGallery([]);
      }
    }
  }, [open, item]);

  const handleField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleGalleryAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!item?.id) {
      toast.warning("Salve o item primeiro para adicionar imagens à galeria");
      e.target.value = "";
      return;
    }
    setGalleryLoading(true);
    let lastGallery = [...gallery];
    let errors = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("image", file);
        const { data } = await uploadGalleryImage(item.id, fd);
        lastGallery = data.gallery || lastGallery;
        setGallery(lastGallery);
        onSaved && onSaved(data);
      } catch {
        errors += 1;
      }
    }
    if (errors > 0) toast.error(`${errors} imagem(ns) não puderam ser enviadas`);
    setGalleryLoading(false);
    e.target.value = "";
  };

  const handleGalleryRemove = async (imgPath) => {
    if (!item?.id) return;
    setGalleryLoading(true);
    try {
      const { data } = await removeGalleryImage(item.id, imgPath);
      setGallery(data.gallery || []);
      onSaved && onSaved(data);
    } catch {
      toast.error("Erro ao remover imagem");
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoFile(file);
    setVideoFileName(file.name);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error("O título é obrigatório"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("shortDescription", form.shortDescription);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("videoUrl", form.videoUrl);
      fd.append("buttonText", form.buttonText || "Saiba Mais");
      fd.append("buttonUrl", form.buttonUrl);
      fd.append("active", form.active ? "true" : "false");
      fd.append("sortOrder", String(form.sortOrder || 0));
      if (coverFile) fd.append("coverImage", coverFile);

      let data;
      if (isEdit) {
        ({ data } = await updateMarketplaceItem(item.id, fd));
      } else {
        ({ data } = await createMarketplaceItem(fd));
      }

      // Upload de vídeo se selecionado
      if (videoFile) {
        const vfd = new FormData();
        vfd.append("video", videoFile);
        await uploadVideoFile(data.id, vfd);
      }

      toast.success(isEdit ? "Item atualizado" : "Item criado");
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>
        {isEdit ? "Editar Item do Marketplace" : "Novo Item do Marketplace"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>

          {/* Título */}
          <Grid item xs={12} sm={8}>
            <TextField
              label="Título *"
              fullWidth
              value={form.title}
              onChange={handleField("title")}
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* Categoria */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Categoria"
              select
              fullWidth
              value={form.category}
              onChange={handleField("category")}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">Selecione...</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Descrição curta */}
          <Grid item xs={12}>
            <TextField
              label="Descrição curta"
              fullWidth
              value={form.shortDescription}
              onChange={handleField("shortDescription")}
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 500 }}
              helperText="Exibida no card de listagem (máx. 500 caracteres)"
            />
          </Grid>

          {/* Descrição longa */}
          <Grid item xs={12}>
            <Typography className={classes.sectionLabel}>Descrição completa</Typography>
            <div className={classes.quillWrapper}>
              <ReactQuill
                theme="snow"
                value={form.description}
                onChange={(val) => setForm((prev) => ({ ...prev, description: val }))}
                style={{ background: "#fff" }}
              />
            </div>
          </Grid>

          {/* Imagem de capa */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionLabel}>Imagem de capa</Typography>
            <input
              type="file"
              accept="image/*"
              ref={coverInputRef}
              style={{ display: "none" }}
              onChange={handleCoverChange}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={() => coverInputRef.current.click()}
              style={{ marginTop: 4 }}
            >
              {coverPreview ? "Trocar imagem" : "Selecionar imagem"}
            </Button>
            {coverPreview && (
              <img src={coverPreview} alt="Capa" className={classes.coverPreview} />
            )}
          </Grid>

          {/* Galeria */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionLabel}>
              Galeria {!isEdit && <span style={{ fontWeight: 400, textTransform: "none" }}>(disponível após criar)</span>}
            </Typography>
            {isEdit && (
              <>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={galleryInputRef}
                  style={{ display: "none" }}
                  onChange={handleGalleryAdd}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={galleryLoading ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
                  onClick={() => galleryInputRef.current.click()}
                  disabled={galleryLoading}
                  style={{ marginTop: 4 }}
                >
                  {galleryLoading ? "Enviando..." : "Adicionar fotos"}
                </Button>
                <div className={classes.galleryGrid}>
                  {gallery.map((imgPath) => (
                    <div key={imgPath} className={classes.galleryThumb}>
                      <img
                        src={`${backendUrl}/public/${imgPath}`}
                        alt=""
                        className={classes.galleryImg}
                      />
                      <IconButton
                        size="small"
                        className={classes.galleryDelete}
                        onClick={() => handleGalleryRemove(imgPath)}
                      >
                        <DeleteIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </div>
                  ))}
                  {gallery.length === 0 && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 6 }}>
                      Nenhuma imagem
                    </Typography>
                  )}
                </div>
              </>
            )}
          </Grid>

          {/* Vídeo URL */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="URL do vídeo (YouTube)"
              fullWidth
              value={form.videoUrl}
              onChange={handleField("videoUrl")}
              variant="outlined"
              size="small"
              placeholder="https://youtube.com/watch?v=..."
            />
          </Grid>

          {/* Vídeo upload */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionLabel}>Arquivo de vídeo</Typography>
            <input
              type="file"
              accept="video/*"
              ref={videoInputRef}
              style={{ display: "none" }}
              onChange={handleVideoChange}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<VideoLibraryIcon />}
              onClick={() => videoInputRef.current.click()}
              style={{ marginTop: 4 }}
            >
              {videoFileName ? "Trocar vídeo" : "Selecionar vídeo"}
            </Button>
            {videoFileName && (
              <Chip
                label={videoFileName}
                size="small"
                color="primary"
                style={{ marginLeft: 8, maxWidth: 180 }}
                onDelete={() => { setVideoFile(null); setVideoFileName(""); }}
              />
            )}
          </Grid>

          {/* Botão */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Texto do botão"
              fullWidth
              value={form.buttonText}
              onChange={handleField("buttonText")}
              variant="outlined"
              size="small"
              placeholder="Saiba Mais"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="URL do botão"
              fullWidth
              value={form.buttonUrl}
              onChange={handleField("buttonUrl")}
              variant="outlined"
              size="small"
              placeholder="https://..."
            />
          </Grid>

          {/* Ordem e ativo */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ordem de exibição"
              type="number"
              fullWidth
              value={form.sortOrder}
              onChange={handleField("sortOrder")}
              variant="outlined"
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => { const checked = e.target.checked; setForm((prev) => ({ ...prev, active: checked })); }}
                  color="primary"
                />
              }
              label="Ativo (visível no marketplace)"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : (isEdit ? "Salvar" : "Criar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppMarketplaceModal;
